import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import React, { PropsWithChildren, Suspense, useRef } from "react";
import { ApiStateMetadata } from "../../../state/Store";
import SettingsPageError from "./SettingsPageError";
import SettingsPageLoading from "./SettingsPageLoading";

interface Props {
    className?: string;
    testId?: string;
    error?: (string | undefined | false)[] | string | false;
    api?: { meta: ApiStateMetadata };
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        padding: "1rem 0.5rem 1rem 0",
        overflow: "auto",
        transition: theme.transitions.create("padding")
    }
}));

const SettingsPageContent: React.FC<PropsWithChildren<Props>> = props => {
    const classes = useStyles();
    const ref = useRef<HTMLDivElement>(null);

    /**
     * If the API is still loading, display the loading indicator. This makes sure that we don't have to check for
     * the loading state when determining a possible error (e.g. user not found would always be displayed while loading).
     *
     * If the API has loaded, display a possible loading error. This way, we won't display the custom error if not
     * all data has been successfully loaded (e.g. user not found would always be displayed if the user API fails to load).
     *
     * If there is a custom error, display it. If it is an array, check for the first truthy value and display that.
     * Otherwise, show the actual content.
     *
     * All content is wrapped in a suspense that hides the entire screen while loading, displaying the usual loading
     * indicator instead.
     */

    let content = props.children;
    if (props.api?.meta && !props.api.meta.finished) {
        content = (
            <SettingsPageLoading />
        );
    } else if (props.api?.meta.error) {
        content = (
            <SettingsPageError
                error={props.api.meta.error}
                retry={props.api.meta.retry} />
        );
    } else if (props.error) {
        const error = Array.isArray(props.error) ? props.error.find(e => !!e) : props.error;
        if (error) {
            content = (
                <SettingsPageError error={error} />
            );
        }
    }

    return (
        <div
            ref={ref}
            data-cy={props.testId}
            className={clsx(classes.root, props.className)}>
            <Suspense fallback={<SettingsPageLoading />}>
                {content}
            </Suspense>
        </div>
    );
};

export default SettingsPageContent;
